import React, { useCallback, useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import StudentTile from './StudentTile';
import { DIFFICULTY_LABELS } from '../../lib/mcq';
import { QBANK_ITEM_TYPE_ICONS } from '../../lib/questionBankItems';

const formatDateOnly = (value) => {
  if (!value) return '';
  try { return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return ''; }
};

const assignmentBadge = (a) => {
  const now = Date.now();
  const due = a.dueAt ? new Date(a.dueAt).getTime() : null;
  if (a.sessionStatus === 'completed') return <Badge bg="success">Completed · {Number.isFinite(a.sessionScore) ? `${a.sessionScore}%` : 'graded'}</Badge>;
  if (due && now > due) return <Badge bg="danger">Past due</Badge>;
  if (due && due - now < 3 * 24 * 60 * 60_000) return <Badge bg="warning" text="dark">Due {formatDateOnly(a.dueAt)}</Badge>;
  if (due) return <Badge bg="info">Due {formatDateOnly(a.dueAt)}</Badge>;
  return <Badge bg="secondary">No due date</Badge>;
};

function ReferralPanel({ token }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/referrals/my-code', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled && d) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const copy = useCallback(() => {
    if (!data?.shareUrl) return;
    navigator.clipboard.writeText(data.shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [data]);

  if (!data) return null;

  return (
    <div className="app-panel p-4 mb-3">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div className="flex-grow-1">
          <h5 className="mb-1">
            <i className="bi bi-gift me-2" style={{ color: 'var(--app-primary)' }} />
            Refer a coursemate
          </h5>
          <p className="text-muted small mb-3">
            Share your link — you both get 7 free days when they subscribe.
          </p>
          <InputGroup style={{ maxWidth: 480 }}>
            <Form.Control
              readOnly
              value={data.shareUrl}
              size="sm"
              style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            <Button variant={copied ? 'success' : 'outline-secondary'} size="sm" onClick={copy}>
              {copied ? <><i className="bi bi-check2 me-1" />Copied</> : <><i className="bi bi-clipboard me-1" />Copy</>}
            </Button>
          </InputGroup>
        </div>
        {(data.rewarded > 0 || data.pending > 0) && (
          <div className="text-end text-muted small mt-1">
            {data.pending > 0 && <div>{data.pending} referred</div>}
            {data.rewarded > 0 && (
              <div className="fw-semibold" style={{ color: 'var(--app-primary)' }}>
                {data.rewarded} reward{data.rewarded !== 1 ? 's' : ''} earned
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const StudentHome = ({
  token,
  tenantType,
  onOpenCases,
  onOpenMcqs,
  onOpenEpma,
  onOpenSpotError,
  onOpenOutpatient,
  onOpenPortfolio,
  sessionInput = '',
  onSessionInputChange,
  onJoinLive,
  assignments = [],
  onStartAssignment,
  onStartRevision,
  revisionBests = {},
  revisionLengthRange = { min: 5, max: 50, step: 5 },
  revisionAvailableCategories = [],
  revisionAvailableDifficulties = [],
  revisionAvailableQuestionTypes = [],
  revisionRecentSessions = [],
  curatedRevisionExercises = [],
  onStartCuratedRevision,
}) => {
  const dueAssignments = assignments.filter((a) => a.sessionStatus !== 'completed');
  const recentCompleted = assignments.filter((a) => a.sessionStatus === 'completed').slice(0, 3);
  const [showRevisionPicker, setShowRevisionPicker] = useState(false);
  // 'curated' = list of lecturer-built sets (the landing view).
  // 'random' = the length/filters/chips for an ad-hoc mixed run.
  const [pickerView, setPickerView] = useState('curated');
  const [curatedSearch, setCuratedSearch] = useState('');
  const [revisionLength, setRevisionLength] = useState(20);
  // Multi-select chips: empty = no filter on that axis.
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState([]);
  const toggleInList = (list, value) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  const openRevisionPicker = () => {
    setRevisionLength(20);
    setSelectedCategories([]);
    setSelectedDifficulties([]);
    setSelectedQuestionTypes([]);
    setCuratedSearch('');
    // Always land on the curated list — the empty state explains the
    // concept and pushes the student towards the random run if their
    // tenant hasn't published any curated sets yet.
    setPickerView('curated');
    setShowRevisionPicker(true);
  };
  const filteredCurated = curatedSearch.trim()
    ? curatedRevisionExercises.filter((ex) => {
        const q = curatedSearch.trim().toLowerCase();
        return String(ex.title || '').toLowerCase().includes(q)
          || String(ex.summary || '').toLowerCase().includes(q)
          || String(ex.category || '').toLowerCase().includes(q);
      })
    : curatedRevisionExercises;
  const startRevision = () => {
    setShowRevisionPicker(false);
    if (typeof onStartRevision === 'function') {
      onStartRevision(revisionLength, {
        categories: selectedCategories,
        difficulties: selectedDifficulties,
        questionTypes: selectedQuestionTypes,
      });
    }
  };
  const QUESTION_TYPE_LABELS = {
    calculation: 'Calculation',
    mcq: 'Multiple choice',
    'true-false': 'True / false',
    'multi-mcq': 'Multi-select',
    cloze: 'Fill in the blanks',
    ordering: 'Put in order',
    'bucket-sort': 'Sort into buckets',
    matching: 'Match pairs',
  };
  const hasFilters =
    revisionAvailableCategories.length > 0
    || revisionAvailableDifficulties.length > 0
    || revisionAvailableQuestionTypes.length > 0;
  const activeFilterCount =
    selectedCategories.length + selectedDifficulties.length + selectedQuestionTypes.length;
  // Personal best for the currently-selected length (or the closest
  // bucket the student has played) so the slider shows live feedback as
  // they drag.
  const currentBest = revisionBests?.[revisionLength]?.score;
  return (
    <div className="student-page">

      <Container className="mt-4 student-page__content ">
        <div className="student-dashboard-shell">
          <div className="student-dashboard-header shadow-sm rounded mb-1">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <h2 className="mb-2">Student workspace</h2>
                <p className="student-dashboard-header__copy mb-0">
                  Choose a study route below to open case studies or access the EPMA system.
                </p>
              </div>

            </div>
          </div>
          {assignments.length ? (
            <Card className="shadow-sm rounded mb-3 student-assignment-board">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <h5 className="mb-0"><i className="bi bi-calendar-check me-2" />Due exercises</h5>
                  <span className="small text-muted">{dueAssignments.length} outstanding</span>
                </div>
                {dueAssignments.length === 0 ? (
                  <div className="small text-muted">Nothing currently due. Great work.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {dueAssignments.map((a) => (
                      <div key={a.id} className="d-flex justify-content-between align-items-center gap-3 flex-wrap student-assignment-row">
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{a.title || a.drillTitle}</div>
                          {a.title ? <div className="small text-muted">{a.drillTitle}</div> : null}
                          {a.instructions ? <div className="small text-muted">{a.instructions}</div> : null}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {assignmentBadge(a)}
                          <Button size="sm" variant="primary" onClick={() => onStartAssignment?.(a)}>
                            {a.sessionStatus === 'in_progress' ? 'Resume' : 'Start'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {recentCompleted.length ? (
                  <div className="mt-3 pt-3 border-top">
                    <div className="small text-muted mb-1">Recently completed</div>
                    <div className="d-flex flex-column gap-1">
                      {recentCompleted.map((a) => (
                        <div key={a.id} className="d-flex justify-content-between align-items-center small">
                          <span>{a.title || a.drillTitle}</span>
                          <span className="text-muted">{Number.isFinite(a.sessionScore) ? `${a.sessionScore}%` : 'graded'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </Card.Body>
            </Card>
          ) : null}

          <div className="student-dashboard-join shadow-sm rounded p-4">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <h5 className="mb-2">Join live teaching session</h5>
                <Form className="mt-3" onSubmit={onJoinLive}>
                  <Form.Label htmlFor="student-live-session-code" className="mb-2">
                    Enter a live code to join a real-time teaching session.
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      id="student-live-session-code"
                      type="text"
                      value={sessionInput}
                      onChange={(event) => onSessionInputChange?.(event.target.value)}
                      placeholder="Enter live session code"
                    />
                    <Button type="submit" variant="primary" disabled={!String(sessionInput || '').trim()}>
                      Join
                    </Button>
                  </InputGroup>
                </Form>
              </div>
            </div>
          </div>

          <Row className="g-4 mb-4">
            <Col xs={6} md={6} lg={3}>
              <StudentTile
                title="ePMA"
                description="Practice prescribing on a hospital-style ePMA simulator."
                icon="bi bi-capsule-pill"
                onClick={onOpenEpma}
                variant="indigo"
              />
            </Col>
            <Col xs={6} md={6} lg={3}>
              <StudentTile
                title="Case studies"
                description="Self-paced inpatient cases prepared by lecturers."
                icon="bi bi-journal-medical"
                onClick={onOpenCases}
                variant="blue"
              />
            </Col>
            {onOpenOutpatient ? (
              <Col xs={6} md={6} lg={3}>
                <StudentTile
                  title="Outpatient cases"
                  description="Write a take-home prescription for an outpatient clinic scenario."
                  icon="bi bi-prescription2"
                  onClick={onOpenOutpatient}
                  variant="emerald"
                />
              </Col>
            ) : null}
            {onOpenMcqs ? (
              <Col xs={6} md={6} lg={3}>
                <StudentTile
                  title="Question bank"
                  description="Recall practice across MCQs, cloze, drag-and-drop, calculations and more — including topic-focused sets."
                  icon="bi bi-ui-checks"
                  onClick={onOpenMcqs}
                  variant="violet"
                />
              </Col>
            ) : null}
            {onOpenSpotError ? (
              <Col xs={6} md={6} lg={3}>
                <StudentTile
                  title="Spot the Error"
                  description="Time-pressured chart review. Flag every error before the clock."
                  icon="bi bi-exclamation-diamond"
                  onClick={onOpenSpotError}
                  variant="rose"
                />
              </Col>
            ) : null}
            {onOpenPortfolio ? (
              <Col xs={6} md={6} lg={3}>
                <StudentTile
                  title="My portfolio"
                  description="Your activity, reflections, and CPD log in one place."
                  icon="bi bi-journal-bookmark"
                  onClick={onOpenPortfolio}
                  variant="cyan"
                />
              </Col>
            ) : null}
            {onStartRevision ? (
              <Col xs={6} md={6} lg={3}>
                <StudentTile
                  title="Mixed revision"
                  description="Random calculations and MCQs from every exercise. Chase your high score."
                  icon="bi bi-shuffle"
                  onClick={openRevisionPicker}
                  variant="indigo"
                />
              </Col>
            ) : null}
          </Row>
          {tenantType === 'central_students' && token ? (
            <ReferralPanel token={token} />
          ) : null}
        </div>
      </Container>

      <Modal show={showRevisionPicker} onHide={() => setShowRevisionPicker(false)} centered size={pickerView === 'curated' ? 'lg' : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>
            {pickerView === 'curated' ? (
              <><i className="bi bi-bookmark-star me-2" />Revision sets</>
            ) : (
              <><i className="bi bi-shuffle me-2" />Random revision run</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pickerView === 'curated' ? (
            <>
              <p className="text-muted small mb-3">
                Lecturer-curated revision sessions. Pick a set to play it now, or build a random run from across every published exercise.
              </p>
              <InputGroup className="mb-3">
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by title, summary, or topic…"
                  value={curatedSearch}
                  onChange={(e) => setCuratedSearch(e.target.value)}
                  autoFocus
                />
                {curatedSearch ? (
                  <Button variant="outline-secondary" onClick={() => setCuratedSearch('')}>Clear</Button>
                ) : null}
              </InputGroup>
              {filteredCurated.length === 0 ? (
                <div className="text-muted text-center p-4 border rounded bg-light">
                  {curatedRevisionExercises.length === 0
                    ? 'No revision sets have been published yet. Try a random run below — your lecturer can add curated sets later.'
                    : 'No revision sets match that search.'}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {filteredCurated.map((ex) => {
                    const qcount = Array.isArray(ex.payload?.templates) ? ex.payload.templates.length : 0;
                    return (
                      <div key={ex.id} className="curated-revision-row d-flex justify-content-between align-items-center gap-2 p-2 border rounded">
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{ex.title}</div>
                          {ex.summary ? <div className="small text-muted">{ex.summary}</div> : null}
                          <div className="small text-muted">
                            {qcount} question{qcount === 1 ? '' : 's'}
                            {ex.category ? <> · {ex.category}</> : null}
                            {ex.difficulty ? <> · {DIFFICULTY_LABELS[ex.difficulty] || ex.difficulty}</> : null}
                            {ex.personalBest ? <> · Best <strong>{ex.personalBest.score}%</strong></> : null}
                          </div>
                        </div>
                        <Button size="sm" variant="primary" onClick={() => { setShowRevisionPicker(false); onStartCuratedRevision?.(ex); }}>
                          <i className="bi bi-play-fill me-1" />Play
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="d-flex justify-content-center mt-4 pt-3 border-top">
                <Button type="button" variant="outline-primary" onClick={() => setPickerView('random')}>
                  <i className="bi bi-shuffle me-1" />Or do a random session
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button type="button" variant="link" className="p-0 mb-2" onClick={() => setPickerView('curated')}>
                <i className="bi bi-arrow-left me-1" />Back to revision sets
              </Button>
              <p className="text-muted small mb-3">
                Picks random questions from across every published exercise and plays them in Challenge mode.
                Streak counter, points and a personal best per length.
              </p>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label htmlFor="mixed-revision-length" className="mb-0 fw-semibold">Length</Form.Label>
            <div className="d-flex align-items-baseline gap-2">
              <span className="fs-3 fw-bold text-primary">{revisionLength}</span>
              <span className="text-muted">questions</span>
            </div>
          </div>
          <Form.Range
            id="mixed-revision-length"
            min={revisionLengthRange.min}
            max={revisionLengthRange.max}
            step={revisionLengthRange.step}
            value={revisionLength}
            onChange={(event) => setRevisionLength(Number(event.target.value))}
          />
          <div className="d-flex justify-content-between text-muted small">
            <span>{revisionLengthRange.min}</span>
            <span>{revisionLengthRange.max}</span>
          </div>
          <div className="mt-3 small text-center">
            {Number.isFinite(currentBest)
              ? <><i className="bi bi-trophy-fill text-warning me-1" />Best at {revisionLength} questions: <strong>{currentBest}%</strong></>
              : <span className="text-muted">No personal best yet at this length.</span>}
          </div>
          {hasFilters ? (
            <div className="revision-filter-panel mt-4 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold">Focus this run <span className="text-muted small fw-normal">(optional)</span></span>
                {activeFilterCount > 0 ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => {
                      setSelectedCategories([]);
                      setSelectedDifficulties([]);
                      setSelectedQuestionTypes([]);
                    }}
                  >
                    Clear ({activeFilterCount})
                  </Button>
                ) : null}
              </div>
              {revisionAvailableQuestionTypes.length ? (
                <div className="mb-3">
                  <div className="small text-muted mb-1">Question type</div>
                  <div className="d-flex flex-wrap gap-2">
                    {revisionAvailableQuestionTypes.map((t) => {
                      const active = selectedQuestionTypes.includes(t);
                      // Calculation isn't in the qbank-item library (it's
                      // owned by the calc module) but appears as a
                      // question type here, so it needs its own glyph.
                      const icon = QBANK_ITEM_TYPE_ICONS[t] || (t === 'calculation' ? 'bi-calculator' : 'bi-question-square');
                      return (
                        <Button
                          key={t}
                          type="button"
                          size="sm"
                          variant={active ? 'primary' : 'outline-primary'}
                          className="revision-filter-chip"
                          onClick={() => setSelectedQuestionTypes((list) => toggleInList(list, t))}
                        >
                          <i className={`bi ${active ? 'bi-check2' : icon} me-1`} aria-hidden="true" />
                          {QUESTION_TYPE_LABELS[t] || t}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {revisionAvailableCategories.length ? (
                <div className="mb-3">
                  <div className="small text-muted mb-1">Topics</div>
                  <div className="d-flex flex-wrap gap-2">
                    {revisionAvailableCategories.map((cat) => {
                      const active = selectedCategories.includes(cat);
                      return (
                        <Button
                          key={cat}
                          type="button"
                          size="sm"
                          variant={active ? 'primary' : 'outline-primary'}
                          className="revision-filter-chip"
                          onClick={() => setSelectedCategories((list) => toggleInList(list, cat))}
                        >
                          {active ? <i className="bi bi-check2 me-1" /> : <i className="bi bi-tag me-1" />}
                          {cat}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {revisionAvailableDifficulties.length ? (
                <div className="mb-3">
                  <div className="small text-muted mb-1">Difficulty</div>
                  <div className="d-flex flex-wrap gap-2">
                    {revisionAvailableDifficulties.map((d) => {
                      const active = selectedDifficulties.includes(d);
                      return (
                        <Button
                          key={d}
                          type="button"
                          size="sm"
                          variant={active ? 'primary' : 'outline-primary'}
                          className="revision-filter-chip"
                          onClick={() => setSelectedDifficulties((list) => toggleInList(list, d))}
                        >
                          {active ? <i className="bi bi-check2 me-1" /> : null}
                          {DIFFICULTY_LABELS[d] || d}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="small text-muted mt-2">
                {activeFilterCount === 0
                  ? 'No filters — questions are drawn from across every published exercise.'
                  : 'Only exercises matching every selected filter will be included.'}
              </div>
            </div>
          ) : null}
          {revisionRecentSessions.length ? (
            <div className="revision-recent-panel mt-3 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-baseline mb-2">
                <h6 className="mb-0">Recent attempts</h6>
                <span className="small text-muted">Last {revisionRecentSessions.length}</span>
              </div>
              <div className="d-flex flex-column gap-1">
                {revisionRecentSessions.map((s) => {
                  const when = s.completedAt ? new Date(s.completedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—';
                  const scoreVariant = s.score >= 80 ? 'success' : s.score >= 50 ? 'warning' : 'danger';
                  return (
                    <div key={s.id} className="d-flex align-items-center justify-content-between revision-recent-row">
                      <div className="d-flex align-items-center gap-2 small">
                        <span className="text-muted" style={{ minWidth: '3.5rem' }}>{when}</span>
                        <span className="text-muted">·</span>
                        <span>{s.length} qs</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {Number.isFinite(s.bestStreak) && s.bestStreak > 0 ? (
                          <span className="small text-muted">
                            <i className="bi bi-fire text-warning me-1" />{s.bestStreak}
                          </span>
                        ) : null}
                        <span className={`badge bg-${scoreVariant}-subtle text-${scoreVariant}-emphasis revision-recent-score`}>
                          {s.score}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={() => setShowRevisionPicker(false)}>
            {pickerView === 'curated' ? 'Close' : 'Cancel'}
          </Button>
          {pickerView === 'random' ? (
            <Button type="button" variant="primary" onClick={startRevision}>
              <i className="bi bi-play-fill me-1" />Start random run
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentHome;
